import { SilverbackPageContext } from '@amazeelabs/gatsby-source-silverback';
import { useLocation } from '@reach/router';
import queryString from 'query-string';
import { useEffect, useState } from 'react';

import { useCurrentUserQuery } from '../../generated/preview-queries';

export const PREVIEW_REFRESH_INTERVAL = 5000;

export const getPreviewRemoteId = ({
  location,
  pageContext,
}: {
  location: Location;
  pageContext: SilverbackPageContext;
}): string => {
  const parsedSearch = queryString.parse(location.search) as {
    id: string;
  };
  const { id } = parsedSearch;
  // Favour the query string id as it might not always be available
  // for new pages, revisions, path changes, ...
  const drupalId = id || pageContext.remoteId;
  return pageContext?.locale && pageContext?.locale !== ''
    ? `${drupalId}:${pageContext.locale}`
    : drupalId;
};

export const getPreviewRevisionId = ({
  location,
}: {
  location: Location;
}): string => {
  const parsedSearch = queryString.parse(location.search) as {
    revision: string;
  };
  const { revision } = parsedSearch;
  return revision || '0';
};

export const usePreviewQuery = () => {
  const { search } = useLocation();
  const [loggedIn, setLoggedIn] = useState(false);
  const parsedQueryString = queryString.parse(search) as {
    preview?: string;
  };
  const { preview } = parsedQueryString;
  useEffect(() => {
    setLoggedIn(document?.cookie?.includes('drupal_user'));
  }, [setLoggedIn]);
  const isPreview = (preview && preview === '1') || loggedIn;
  const currentUser = useCurrentUserQuery({}, { enabled: isPreview });
  if (!isPreview || currentUser.isLoading || currentUser.isError) {
    return false;
  } else {
    return (
      isPreview &&
      currentUser.isSuccess &&
      currentUser?.data?.currentUser?.id &&
      // Shouldn't even reach this stage if anonymous
      // as anonymous users shouldn't be able to
      // execute arbitrary queries. Keeping this test
      // as a safeguard to prevent permissions misconfiguration as
      // there shouldn't be use cases for anonymous preview.
      currentUser?.data?.currentUser?.id !== '0'
    );
  }
};

export function useCurrentPageInfo() {
  const [info, setInfo] = useState<{
    type?: string;
    id?: string;
    path?: string;
  }>({});
  useEffect(() => {
    // @ts-ignore
    if (window.___pageId !== '___PAGE_ID___') {
      setInfo({
        // @ts-ignore
        type: window.___pageType,
        // @ts-ignore
        id: window.___pageId,
        // @ts-ignore
        path: window.___pagePath,
      });
    }
  }, []);
  return info;
}

type ContentQueryVariables = { remoteId: string };
type ContentRevisionQueryVariables = { remoteId: string; revisionId: string };
type ContentQueryOptions = {
  enabled: boolean;
  refetchInterval: false | number;
  refetchOnWindowFocus?: boolean;
  refetchOnMount?: boolean;
  refetchOnReconnect?: boolean;
};

export function useContent<T>(
  useContentQuery: (
    vars: ContentQueryVariables,
    opts: ContentQueryOptions,
  ) => { data?: { content?: T }; refetch: () => void },
  useContentRevisionQuery: (
    vars: ContentRevisionQueryVariables,
    opts: ContentQueryOptions,
  ) => { data?: { content?: T }; refetch: () => void },
  staticContent: T | undefined,
  pageContext: SilverbackPageContext,
) {
  const location = useLocation();
  const usePreview = usePreviewQuery() || false;
  const usePreviewRevision =
    usePreview && getPreviewRevisionId({ location }) !== '0';

  const previewRevisionContent = useContentRevisionQuery(
    {
      remoteId: getPreviewRemoteId({ location, pageContext }),
      revisionId: getPreviewRevisionId({ location }),
    },
    {
      refetchInterval: false,
      enabled: usePreviewRevision,
    },
  );

  const previewContent = useContentQuery(
    {
      remoteId: getPreviewRemoteId({ location, pageContext }),
    },
    {
      refetchInterval: false,
      // Enable the preview query only
      // when the revision query is not enabled.
      enabled: usePreview && !usePreviewRevision,
    },
  );

  useEffect(() => {
    const url =
      process.env.GATSBY_CHANGES_WS_URL ||
      'ws://localhost:3000/___status/changes';
    const ws = new WebSocket(url);
    ws.onmessage = () => {
      // TODO: Filter for updates relevant to this page.
      if (previewContent.data) {
        previewContent.refetch();
      }
      if (previewRevisionContent.data) {
        previewRevisionContent.refetch();
      }
    };
    return () => ws.close();
  }, [previewContent, previewRevisionContent]);

  const info = useCurrentPageInfo();
  const csrContent = useContentQuery(
    {
      remoteId: info.id || getPreviewRemoteId({ location, pageContext }),
    },
    {
      enabled: !usePreview && !staticContent,
      refetchInterval: false,
    },
  );
  return (
    previewRevisionContent.data?.content ||
    previewContent.data?.content ||
    csrContent.data?.content ||
    staticContent
  );
}
